import { PrimaryTemplate } from 'components/templates/PrimaryTemplate';
import { mergeClasses } from 'util/commons';
import styles from './SelectTenant.module.scss';
import SelectTenantCard, {
  Tenant,
} from 'components/molecules/SelectTenantCard';
import SelectSectionCard, {
  Section,
} from 'components/molecules/SelectSectionCard';
import { useHistory } from 'react-router';
import { Path } from 'routes';
import { useTranslation } from 'react-i18next';
import SelectFloorCard, { Floor } from 'components/molecules/SelectFloorCard';
import { useSelector } from 'react-redux';
import { selectUser, UserState } from 'redux/slices/userSlice';
import { OperatorType, RoleType } from 'util/Enums';
import { IconSearch } from 'components/atoms/icons/IconSearch';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { useQuery } from 'util/queryParams';

type Props = {
  sections: Section[];
  oSections: Section[];
  tenants: Tenant[];
  oTenants: Tenant[];
  selectedSection?: Section;
  onSelectSection: (section: Section) => void;
  selectedFloor?: Floor;
  onSelectFloor: (floor: Floor) => void;
  hasLogo?: boolean;
  showActions?: boolean;
  onSelectTenant: (tenant: Tenant) => void;
  onClickReload?: (force: boolean) => void;
  prev?: () => void;
  withPageReload?: boolean;
};

export const isSortableAllowed = (user: UserState) => {
  return (
    user.role === RoleType.AdminSite ||
    user.operatorType === OperatorType.Compressions ||
    user.operatorType === OperatorType.Recycle
  );
};

export const SelectTenant = ({
  sections,
  oSections,
  tenants,
  oTenants,
  selectedSection,
  onSelectSection,
  selectedFloor,
  onSelectFloor,
  hasLogo = true,
  showActions = true,
  onSelectTenant,
  onClickReload,
  prev,
  withPageReload = true,
}: Props) => {
  const history = useHistory();
  const { t } = useTranslation();
  const user = useSelector(selectUser);
  const [searchTenants, setSearchTenants] = useState<Tenant[]>([]);
  const searchKeyword = useQuery().get('keyword')?.trim();
  const [filter, setFilter] = useState<{ keyword?: string }>({});
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchKeyword && inputRef.current) {
      inputRef.current.value = searchKeyword;
      setFilter({ keyword: searchKeyword });
    }
  }, [searchKeyword]);

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();

    const value = inputRef.current?.value?.trim() || '';
    const searchParams = new URLSearchParams(history.location.search);

    if (value) {
      searchParams.set('keyword', value);
    } else {
      searchParams.delete('keyword');
    }

    setFilter({ keyword: value });
    history.push({
      pathname: history.location.pathname,
      search: searchParams.toString(),
    });
  };

  const handleReload = () => {
    if (onClickReload) {
      onClickReload(true);
      setSearchTenants([]);
      setFilter({});

      const searchParams = new URLSearchParams(history.location.search);
      searchParams.delete('keyword');
      history.push({
        pathname: history.location.pathname,
        search: searchParams.toString(),
      });

      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  };

  useEffect(() => {
    const dataSubset = [...tenants, ...oTenants].filter(({ name }) => {
      const searchKeyword = filter.keyword?.trim().toLowerCase();

      return (
        !searchKeyword ||
        [name].some((e) => e?.toLowerCase().includes(searchKeyword))
      );
    });

    setSearchTenants(dataSubset);
  }, [filter, tenants, oTenants]);

  return (
    <PrimaryTemplate
      hasLogo={hasLogo}
      prevFunction={prev}
      showActions={showActions}
      withPageReload={withPageReload}
    >
      <div className={styles.container}>
        <form className={styles.searchWrapper}>
          <div className={styles.searchBox}>
            <IconSearch />
            <input
              placeholder={t('common.placeholder.search') || ''}
              ref={inputRef}
            />
          </div>
          <button
            className={styles.buttonSearch}
            type="submit"
            onClick={(e: FormEvent) => handleSearch(e)}
          >
            {t('common.placeholder.search')}
          </button>
        </form>

        {showActions && (
          <div className={mergeClasses(styles.skip, styles.skipRight)}>
            {isSortableAllowed(user) && (
              <button
                onClick={() => {
                  history.push({
                    pathname: Path.emissionNew,
                    search: history.location.search,
                  });
                }}
              >
                {t('common.button.sort_category')}
              </button>
            )}

            <button onClick={handleReload}>{t('common.button.reload')}</button>
          </div>
        )}
        <div className={styles.body}>
          <p className={styles.title}>{t('select_tenant.title')}</p>

          {!filter.keyword && (
            <>
              {sections.length > 0 && (
                <>
                  <div className={styles.sectionList}>
                    {sections?.map((i) => {
                      return (
                        <SelectSectionCard
                          key={i.id}
                          section={i}
                          isSelected={i.id === selectedSection?.id}
                          onSelectSection={onSelectSection}
                        />
                      );
                    })}
                  </div>

                  {selectedSection?.floors &&
                  selectedSection?.floors?.length > 0 ? (
                    <div className={styles.floorList}>
                      {selectedSection?.floors?.map((i) => {
                        return (
                          <SelectFloorCard
                            key={i.id}
                            floor={i}
                            isSelected={i.id === selectedFloor?.id}
                            onSelectFloor={onSelectFloor}
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <div className={styles.tenant}>
                      <div className={styles.tenantContainer}>
                        {tenants
                          .filter(
                            (item) =>
                              item.section &&
                              item.section.id === selectedSection?.id
                          )
                          .map((i) => {
                            return (
                              <SelectTenantCard
                                onSelectTenant={onSelectTenant}
                                tenant={i}
                                key={i.id}
                              />
                            );
                          })}
                      </div>
                    </div>
                  )}
                  <div className={styles.tenant}>
                    <div className={styles.tenantContainer}>
                      {selectedFloor?.tenants?.map((i) => {
                        return (
                          <SelectTenantCard
                            onSelectTenant={onSelectTenant}
                            tenant={i}
                            key={i.id}
                          />
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
              {sections.length === 0 && oSections.length > 0 && (
                <>
                  <div className={styles.sectionList}>
                    {oSections?.map((i) => {
                      return (
                        <SelectSectionCard
                          key={i.id}
                          section={i}
                          isSelected={i.id === selectedSection?.id}
                          onSelectSection={onSelectSection}
                        />
                      );
                    })}
                  </div>
                  <div className={styles.tenant}>
                    <div className={styles.tenantContainer}>
                      {selectedSection?.tenants?.map((i) => {
                        return (
                          <SelectTenantCard
                            onSelectTenant={onSelectTenant}
                            tenant={i}
                            key={i.id}
                          />
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
              {sections.length === 0 && oSections.length === 0 && (
                <div className={styles.tenant}>
                  <div className={styles.tenantContainer}>
                    {oTenants?.map((i) => {
                      return (
                        <SelectTenantCard
                          onSelectTenant={onSelectTenant}
                          tenant={i}
                          key={i.id}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          {filter.keyword && (
            <div className={styles.tenant}>
              <div className={styles.tenantContainer}>
                {searchTenants.map((i) => {
                  return (
                    <SelectTenantCard
                      onSelectTenant={onSelectTenant}
                      tenant={i}
                      displaySectionFloor
                      key={i.id}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </PrimaryTemplate>
  );
};
